import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { admin, type ConfigEntry, type KsefCertInfo } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { validateUploadSize } from '@/lib/uploadLimits';
import { useAuth } from '@/auth/AuthContext';
import { hasPermission } from '@/types';

type Tab = 'AI_TASKS' | 'ASSISTANT' | 'PRICING' | 'CLAUDE' | 'GEMINI' | 'EMBEDDING' | 'DRIVE' | 'MAIL' | 'KSEF' | 'ERSTE' | 'GPS' | 'OCR' | 'MAPS' | 'DOCUMENT_NUMBERING';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'AI_TASKS', label: 'AI · zadania', icon: 'sparkles' },
  { id: 'ASSISTANT', label: 'Asystent AI', icon: 'sparkles' },
  { id: 'PRICING', label: 'Cennik AI', icon: 'file-text' },
  { id: 'CLAUDE', label: 'Claude API', icon: 'sparkles' },
  { id: 'GEMINI', label: 'Google Gemini', icon: 'sparkles' },
  { id: 'EMBEDDING', label: 'Embeddingi', icon: 'search' },
  { id: 'DRIVE', label: 'Google Drive', icon: 'cloud' },
  { id: 'MAIL', label: 'SMTP / E-mail', icon: 'file-text' },
  { id: 'KSEF', label: 'KSeF (faktury)', icon: 'file-text' },
  { id: 'ERSTE', label: 'Erste Bank', icon: 'cloud' },
  { id: 'GPS', label: 'GPS floty', icon: 'truck' },
  { id: 'OCR', label: 'OCR / inne', icon: 'settings' },
  { id: 'MAPS', label: 'Warstwy mapy', icon: 'map' },
  { id: 'DOCUMENT_NUMBERING', label: 'Numerator dokumentów', icon: 'file-text' },
];

export function AdminConfig() {
  // Zapis kluczy oznaczonych jako sekret wymaga ADMIN_SECRETS — bez tej samej bramki we froncie
  // pola byłyby edytowalne, a zapis kończył się odmową (i to po zapisaniu kluczy niesekretnych).
  const { user } = useAuth();
  const mozeSekrety = hasPermission(user, 'ADMIN_SECRETS');
  const [searchParams] = useSearchParams();
  const initialTab = TABS.some(t => t.id === searchParams.get('tab'))
    ? (searchParams.get('tab') as Tab) : 'CLAUDE';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [entries, setEntries] = useState<ConfigEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [testMailTo, setTestMailTo] = useState('');

  useEffect(() => { void load(); }, [tab]);

  async function load() {
    setLoading(true); setMsg(null);
    try {
      const data = await admin.config.list(tab);
      setEntries(data);
      const initialDraft: Record<string, string> = {};
      data.forEach(e => { initialDraft[e.key] = e.value; });
      setDraft(initialDraft);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setBusy(true); setMsg(null);
    try {
      const zmiany = Object.entries(draft)
        .filter(([key, value]) => {
          const orig = entries.find(e => e.key === key);
          // Pomiń jeśli user nie edytował sekretu (zostawił "********")
          if (orig?.secret && value === '********') return false;
          return (orig?.value ?? '') !== value;
        })
        .map(([key, value]) => ({ key, value }));
      if (zmiany.length === 0) {
        setMsg({ type: 'ok', text: 'Brak zmian do zapisania.' });
        setBusy(false);
        return;
      }
      const res = await admin.config.bulk(zmiany);
      setMsg({ type: 'ok', text: `Zapisano ${res.zaktualizowane} zmian${res.pominiete ? ', pominięto ' + res.pominiete : ''}.` });
      await load();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? 'Błąd zapisu' });
    } finally { setBusy(false); }
  }

  async function runTest(which: 'mail' | 'claude' | 'gemini' | 'drive' | 'gps') {
    setBusy(true); setMsg(null);
    try {
      let result: Record<string, unknown>;
      if (which === 'mail') {
        if (!testMailTo.trim()) {
          setMsg({ type: 'err', text: 'Podaj adres e-mail odbiorcy' });
          setBusy(false);
          return;
        }
        result = await admin.config.testMail(testMailTo);
      } else if (which === 'claude') {
        result = await admin.config.testClaude();
      } else if (which === 'gemini') {
        result = await admin.config.testGemini();
      } else if (which === 'gps') {
        result = await admin.config.testGps();
      } else {
        result = await admin.config.testDrive();
      }
      const ok = Boolean(result.ok);
      const details = JSON.stringify(result, null, 2);
      setMsg({ type: ok ? 'ok' : 'err', text: (ok ? 'Test OK. ' : 'Test nieudany. ') + '\n' + details });
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message ?? 'Błąd testu' });
    } finally { setBusy(false); }
  }

  const visible = useMemo(() => entries, [entries]);

  return (
    <div className="max-w-[1100px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Konfiguracja systemu</h1>
          <div className="text-ink-400 text-[13px]">Ustawienia Claude API, Google Drive, SMTP. Wartości sekretne są szyfrowane AES-256-GCM.</div>
        </div>
      </header>

      <div className="flex gap-2 mb-5 border-b border-ink-100">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'flex items-center gap-2 px-4 py-2 text-[13px] font-medium border-b-2 -mb-px ' +
              (tab === t.id ? 'border-water-500 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-800')
            }
          >
            <Icon name={t.icon} className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={'mb-4 p-3 rounded-md text-[12px] whitespace-pre-wrap font-mono ' +
          (msg.type === 'ok' ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500')}>
          {msg.text}
        </div>
      )}

      {tab === 'AI_TASKS' && <AiTasksPanel onSaved={msg => setMsg(msg)} />}
      {tab === 'PRICING' && <PricingPanel onSaved={msg => setMsg(msg)} />}
      {tab === 'EMBEDDING' && <EmbeddingPanel onSaved={msg => setMsg(msg)} />}
      {tab === 'MAPS' && <MapsLayersPanel onSaved={msg => setMsg(msg)} />}
      {tab === 'DOCUMENT_NUMBERING' && <DocumentNumberingPanel onSaved={msg => setMsg(msg)} />}
      {tab === 'KSEF' && <KsefPanel onSaved={msg => setMsg(msg)} />}

      {tab !== 'AI_TASKS' && tab !== 'PRICING' && tab !== 'EMBEDDING' && tab !== 'MAPS' && tab !== 'DOCUMENT_NUMBERING' && tab !== 'KSEF' && (loading ? (
        <div className="text-ink-400">Ładowanie…</div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="space-y-3">
              {visible.map(e => (
                <div key={e.key} className="grid grid-cols-[320px_1fr_auto] gap-3 items-start">
                  <div className="pt-1.5">
                    <div className="font-mono text-[12px] text-ink-700">{e.key}</div>
                    {e.description && <div className="text-[11px] text-ink-400 mt-0.5">{e.description}</div>}
                    {e.secret && (
                      <div className="text-[10px] text-ai-600 mt-1">
                        🔒 Secret (AES-256-GCM){!mozeSekrety && ' · tylko do odczytu'}
                      </div>
                    )}
                    {e.ustawiony && e.secret && (
                      <div className="text-[10px] text-green-500 mt-0.5">✓ Ustawione</div>
                    )}
                  </div>
                  <div>
                    {(e.key.includes('key') || e.key.includes('.p12')) && e.secret ? (
                      <textarea
                        value={draft[e.key] ?? ''}
                        onChange={ev => setDraft({ ...draft, [e.key]: ev.target.value })}
                        className="textarea font-mono text-[12px]"
                        rows={e.key.includes('service-account') || e.key.includes('.p12') ? 10 : 2}
                        disabled={e.secret && !mozeSekrety}
                        placeholder={e.secret && !mozeSekrety ? 'Brak uprawnienia „Klucze API, hasło SMTP, certyfikat KSeF"'
                          : (e.key.includes('.p12') ? 'Wklej certyfikat PKCS#12 zakodowany Base64' : (e.secret ? 'Wprowadź wartość (zostanie zaszyfrowana)' : ''))}
                      />
                    ) : (
                      <input
                        value={draft[e.key] ?? ''}
                        onChange={ev => setDraft({ ...draft, [e.key]: ev.target.value })}
                        className={'input ' + (e.secret ? 'font-mono' : '')}
                        type={e.secret ? 'password' : 'text'}
                        disabled={e.secret && !mozeSekrety}
                        placeholder={e.secret && !mozeSekrety ? 'Brak uprawnienia do sekretów' : (e.secret ? 'Wprowadź wartość' : '')}
                      />
                    )}
                  </div>
                  <div className="pt-1.5 text-[11px] text-ink-400 min-w-[120px]">
                    {e.modifiedBy && <div>przez {e.modifiedBy}</div>}
                    {e.updatedAt && <div>{new Date(e.updatedAt).toLocaleString('pl-PL')}</div>}
                  </div>
                </div>
              ))}
            </div>

            <hr className="border-0 border-t border-ink-100 my-5" />

            <div className="flex items-center gap-3 flex-wrap">
              <button disabled={busy} onClick={() => void save()} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5" /> Zapisz zmiany
              </button>

              {tab === 'MAIL' && (
                <>
                  <input
                    type="email"
                    value={testMailTo}
                    onChange={e => setTestMailTo(e.target.value)}
                    placeholder="odbiorca@hydro-wolski.pl"
                    className="input max-w-[280px]"
                  />
                  <button disabled={busy} onClick={() => void runTest('mail')} className="btn btn-water btn-sm">
                    <Icon name="arrow" className="w-3.5 h-3.5" /> Wyślij testowego maila
                  </button>
                </>
              )}

              {tab === 'CLAUDE' && (
                <button disabled={busy} onClick={() => void runTest('claude')} className="btn btn-ai btn-sm">
                  <Icon name="sparkles" className="w-3.5 h-3.5" /> Testuj Claude API
                </button>
              )}

              {tab === 'GEMINI' && (
                <button disabled={busy} onClick={() => void runTest('gemini')} className="btn btn-ai btn-sm">
                  <Icon name="sparkles" className="w-3.5 h-3.5" /> Testuj Gemini API
                </button>
              )}

              {tab === 'DRIVE' && (
                <button disabled={busy} onClick={() => void runTest('drive')} className="btn btn-water btn-sm">
                  <Icon name="cloud" className="w-3.5 h-3.5" /> Testuj Drive
                </button>
              )}

              {tab === 'GPS' && (
                <button disabled={busy} onClick={() => void runTest('gps')} className="btn btn-water btn-sm">
                  <Icon name="truck" className="w-3.5 h-3.5" /> Testuj połączenie GPS
                </button>
              )}

              <div className="flex-1" />
              <div className="text-[11px] text-ink-400">
                {tab === 'CLAUDE' && 'Dostępne tryby (claude.mode): mock | claude | ollama | gemini. Aby aktywować Gemini → set claude.mode=gemini.'}
                {tab === 'GEMINI' && 'Klucz API z https://aistudio.google.com/apikey. Models: gemini-2.5-pro (jakość) / gemini-2.5-flash (szybkość/koszt).'}
                {tab === 'DRIVE' && 'Tryby: mock | service-account (wymaga JSON klucza)'}
                {tab === 'MAIL' && 'MailHog (dev): host=mailhog, port=1025 | Produkcja: Twój SMTP'}
                {tab === 'GPS' && 'Tryby (gps.mode): off | atlas | mock. Login i hasło z panelu dostawcy lokalizacji (protokół Atlas — u nas Solid Security); login jest ten sam, który występuje w adresie API. Po zapisaniu użyj „Testuj połączenie GPS", a pojazdy pokażą się jako warstwa na Mapie przeglądu.'}
                {tab === 'ERSTE' && 'Tryby (erste.mode): mock | test | prod | off. Dane z developer.erstegroup.com (OAuth2/George): client-id, client-secret, refresh-token, web-api-key, IBAN. Synchronizacja z zakładki „Bank / rozliczenia".'}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// AI Tasks Panel — admin wybiera provider + model per zadanie
// =============================================================================

interface AiTasksPanelProps {
  onSaved: (msg: { type: 'ok' | 'err'; text: string }) => void;
}

interface AiCatalog {
  tasks: { code: string; name: string; description: string }[];
  providers: string[];
  modelsByProvider: Record<string, string[]>;
}

function AiTasksPanel({ onSaved }: AiTasksPanelProps) {
  const [catalog, setCatalog] = useState<AiCatalog | null>(null);
  const [globalMode, setGlobalMode] = useState('mock');
  const [taskValues, setTaskValues] = useState<Record<string, { provider: string; model: string }>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [liveModels, setLiveModels] = useState<Record<string, string[]>>({});
  const [discovering, setDiscovering] = useState<string | null>(null);
  const [discoveryError, setDiscoveryError] = useState<Record<string, string>>({});

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [cat, claudeCfg, taskCfg] = await Promise.all([
        admin.config.aiCatalog(),
        admin.config.list('CLAUDE'),
        admin.config.list('AI_TASKS'),
      ]);
      setCatalog(cat);
      const mode = claudeCfg.find(e => e.key === 'claude.mode');
      setGlobalMode(mode?.value ?? 'mock');
      const vals: Record<string, { provider: string; model: string }> = {};
      for (const t of cat.tasks) {
        vals[t.code] = {
          provider: taskCfg.find(e => e.key === `ai.task.${t.code}.provider`)?.value ?? '',
          model:    taskCfg.find(e => e.key === `ai.task.${t.code}.model`)?.value ?? '',
        };
      }
      setTaskValues(vals);
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? String(e) });
    } finally { setLoading(false); }
  }

  function setTask(code: string, patch: Partial<{ provider: string; model: string }>) {
    setTaskValues(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }));
  }

  async function save() {
    if (!catalog) return;
    setBusy(true);
    try {
      const changes: { key: string; value: string }[] = [
        { key: 'claude.mode', value: globalMode },
      ];
      for (const t of catalog.tasks) {
        changes.push({ key: `ai.task.${t.code}.provider`, value: taskValues[t.code]?.provider ?? '' });
        changes.push({ key: `ai.task.${t.code}.model`,    value: taskValues[t.code]?.model ?? '' });
      }
      const r = await admin.config.bulk(changes);
      onSaved({ type: 'ok', text: `Zapisano. Zaktualizowane: ${r.zaktualizowane}, pominięte: ${r.pominiete}` });
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function discoverModels(provider: string) {
    if (!provider || provider === 'mock') return;
    setDiscovering(provider);
    setDiscoveryError(prev => ({ ...prev, [provider]: '' }));
    try {
      const r = await admin.config.discoverModels(provider);
      if (r.error) {
        setDiscoveryError(prev => ({ ...prev, [provider]: r.error! }));
      } else {
        setLiveModels(prev => ({ ...prev, [provider]: r.models.map(m => m.id) }));
        onSaved({ type: 'ok', text: `Pobrano ${r.models.length} modeli z ${provider}${r.cached ? ' (z cache)' : ''}.` });
      }
    } catch (e: any) {
      setDiscoveryError(prev => ({ ...prev, [provider]: e?.message ?? String(e) }));
    } finally {
      setDiscovering(null);
    }
  }

  if (loading || !catalog) return <div className="text-ink-400">Ładowanie katalogu AI…</div>;

  // Live models override hardcoded jeśli są pobrane
  const allModels = (provider: string): string[] =>
    liveModels[provider] ?? catalog.modelsByProvider[provider] ?? [];

  return (
    <div className="space-y-5">
      {/* Globalny default */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Domyślny provider AI (fallback)</div>
        </div>
        <div className="card-body">
          <p className="text-[12px] text-ink-500 mb-3">
            Używany dla wszystkich zadań które poniżej mają puste pole provider. Klucz <code>claude.mode</code>.
          </p>
          <div className="flex gap-2">
            {catalog.providers.map(p => (
              <button
                key={p}
                onClick={() => setGlobalMode(p)}
                className={'btn btn-sm ' + (globalMode === p ? 'btn-primary' : 'btn-secondary')}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Discovery — pobierz live modele */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Modele dostępne w API</div>
        </div>
        <div className="card-body">
          <p className="text-[12px] text-ink-500 mb-3">
            Domyślnie pokazujemy hardcoded listę. Kliknij <strong>Pobierz z API</strong> aby pokazać pełną aktualną listę z providera (cache 1h).
          </p>
          <div className="flex flex-wrap gap-2">
            {catalog.providers.filter(p => p !== 'mock').map(p => {
              const live = liveModels[p];
              const err = discoveryError[p];
              return (
                <div key={p} className="flex items-center gap-2 border border-ink-200 rounded p-2">
                  <span className="text-[12px] font-semibold w-16">{p}</span>
                  <button onClick={() => void discoverModels(p)} disabled={discovering === p}
                    className="btn btn-secondary btn-sm">
                    <Icon name="sparkles" className="w-3.5 h-3.5"/>
                    {discovering === p ? 'Pobieram…' : (live ? `↻ Odśwież (${live.length})` : 'Pobierz z API')}
                  </button>
                  {err && <span className="text-[10px] text-red-500" title={err}>✗ {err.slice(0, 50)}</span>}
                  {live && <span className="text-[10px] text-green-700">{live.length} modeli</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per zadanie */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Provider i model per zadanie AI</div>
          <div className="text-[11px] text-ink-400 ml-auto">Pusty wybór = użyj domyślnego ({globalMode})</div>
        </div>
        <div className="card-body !p-0">
          <table className="table-hw">
            <thead>
              <tr>
                <th>Zadanie</th>
                <th className="w-44">Provider</th>
                <th className="w-72">Model</th>
                <th>Opis</th>
              </tr>
            </thead>
            <tbody>
              {catalog.tasks.map(t => {
                const v = taskValues[t.code] ?? { provider: '', model: '' };
                const effectiveProvider = v.provider || globalMode;
                const models = allModels(effectiveProvider);
                return (
                  <tr key={t.code}>
                    <td>
                      <div className="font-semibold text-ink-900 text-[13px]">{t.name}</div>
                      <div className="text-[10px] text-ink-400 font-mono">{t.code}</div>
                    </td>
                    <td>
                      <SearchableSelect
                        value={v.provider}
                        onChange={v => setTask(t.code, { provider: v, model: '' })}
                      >
                        <option value="">— domyślny ({globalMode}) —</option>
                        {catalog.providers.map(p => <option key={p} value={p}>{p}</option>)}
                      </SearchableSelect>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <SearchableSelect
                          value={v.model}
                          onChange={v => setTask(t.code, { model: v })}
                          className="flex-1"
                        >
                          <option value="">— domyślny —</option>
                          {models.map(m => <option key={m} value={m}>{m}</option>)}
                        </SearchableSelect>
                        <input
                          value={v.model}
                          onChange={e => setTask(t.code, { model: e.target.value })}
                          className="input flex-1 font-mono text-[11px]"
                          placeholder="lub wpisz custom"
                        />
                      </div>
                    </td>
                    <td className="text-[11px] text-ink-500">{t.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={() => void load()} disabled={busy} className="btn btn-secondary">
          Odśwież
        </button>
        <button onClick={() => void save()} disabled={busy} className="btn btn-primary">
          <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz konfigurację'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Pricing Panel — manualnie utrzymywane ceny per model (Google/Anthropic nie mają API cen)
// =============================================================================

interface PricingPanelProps {
  onSaved: (msg: { type: 'ok' | 'err'; text: string }) => void;
}

interface PricingRow {
  provider: string;
  model: string;
  inputKey: string;
  outputKey: string;
  inputPrice: string;
  outputPrice: string;
  updatedAt?: string;
}

const PRICING_LINKS: Record<string, { url: string; label: string }> = {
  CLAUDE: { url: 'https://www.anthropic.com/pricing', label: 'anthropic.com/pricing' },
  GEMINI: { url: 'https://ai.google.dev/pricing', label: 'ai.google.dev/pricing' },
};

function PricingPanel({ onSaved }: PricingPanelProps) {
  const [entries, setEntries] = useState<ConfigEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [claude, gemini] = await Promise.all([
        admin.config.list('CLAUDE'),
        admin.config.list('GEMINI'),
      ]);
      const all = [...claude, ...gemini].filter(e => e.key.includes('pricing'));
      setEntries(all);
      const d: Record<string, string> = {};
      all.forEach(e => { d[e.key] = e.value; });
      setDraft(d);
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setLoading(false); }
  }

  function rowsByProvider(): Record<string, PricingRow[]> {
    const out: Record<string, PricingRow[]> = {};
    // Z kluczy claude.pricing.{model}.{input|output} budujemy tabelę
    const map = new Map<string, { input?: ConfigEntry; output?: ConfigEntry }>();
    for (const e of entries) {
      const m = e.key.match(/^(claude|gemini)\.pricing\.([^.]+)\.(input|output)$/);
      if (!m) continue;
      const k = `${m[1]}|${m[2]}`;
      const cur = map.get(k) ?? {};
      if (m[3] === 'input') cur.input = e; else cur.output = e;
      map.set(k, cur);
    }
    for (const [k, v] of map.entries()) {
      const [provider, model] = k.split('|');
      const upper = provider.toUpperCase();
      if (!out[upper]) out[upper] = [];
      out[upper].push({
        provider: upper,
        model,
        inputKey: v.input?.key ?? '',
        outputKey: v.output?.key ?? '',
        inputPrice: draft[v.input?.key ?? ''] ?? '',
        outputPrice: draft[v.output?.key ?? ''] ?? '',
        updatedAt: v.input?.updatedAt ?? v.output?.updatedAt,
      });
    }
    return out;
  }

  async function save() {
    setBusy(true);
    try {
      const changes = Object.entries(draft)
        .filter(([key, value]) => entries.find(e => e.key === key)?.value !== value)
        .map(([key, value]) => ({ key, value }));
      if (changes.length === 0) {
        onSaved({ type: 'ok', text: 'Brak zmian.' });
        return;
      }
      const r = await admin.config.bulk(changes);
      onSaved({ type: 'ok', text: `Zaktualizowano ${r.zaktualizowane} cen.` });
      await load();
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="text-ink-400">Ładowanie cennika…</div>;
  const grouped = rowsByProvider();

  return (
    <div className="space-y-5">
      <div className="card border-amber-500/30">
        <div className="card-body text-[12px] text-ink-700">
          <div className="font-semibold text-ink-900 mb-1">⚠ Ceny utrzymywane manualnie</div>
          <p>Żaden provider AI nie udostępnia cen przez API. Aktualne stawki sprawdź na oficjalnych cennikach i wpisz tutaj. Dotyczy to wszystkich modeli (Claude, Gemini, OpenAI, ...).</p>
        </div>
      </div>

      {Object.entries(grouped).map(([provider, rows]) => (
        <div key={provider} className="card">
          <div className="card-head">
            <div className="card-title">{provider}</div>
            {PRICING_LINKS[provider] && (
              <a href={PRICING_LINKS[provider].url} target="_blank" rel="noreferrer"
                className="ml-auto text-[12px] text-water-600 hover:text-water-700">
                📋 {PRICING_LINKS[provider].label} ↗
              </a>
            )}
          </div>
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-32">Model</th>
                  <th className="w-40 text-right">Input ($/1M tok)</th>
                  <th className="w-40 text-right">Output ($/1M tok)</th>
                  <th className="text-right">Stosunek out/in</th>
                  <th className="text-right">Ostatnio</th>
                </tr>
              </thead>
              <tbody>
                {rows.sort((a, b) => a.model.localeCompare(b.model)).map(r => {
                  const inP = parseFloat(r.inputPrice) || 0;
                  const outP = parseFloat(r.outputPrice) || 0;
                  const ratio = inP > 0 ? (outP / inP).toFixed(1) + 'x' : '—';
                  return (
                    <tr key={r.provider + r.model}>
                      <td>
                        <div className="font-mono text-[12px] font-semibold">{r.model}</div>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-ink-400">$</span>
                          <input value={r.inputPrice}
                            onChange={e => setDraft({ ...draft, [r.inputKey]: e.target.value })}
                            className="input w-24 text-right font-mono"/>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-ink-400">$</span>
                          <input value={r.outputPrice}
                            onChange={e => setDraft({ ...draft, [r.outputKey]: e.target.value })}
                            className="input w-24 text-right font-mono"/>
                        </div>
                      </td>
                      <td className="text-right text-[12px] text-ink-500 font-mono">{ratio}</td>
                      <td className="text-right text-[10px] text-ink-400">
                        {r.updatedAt ? r.updatedAt.slice(0, 10) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="flex justify-end gap-2">
        <button onClick={() => void load()} disabled={busy} className="btn btn-secondary">Odśwież</button>
        <button onClick={() => void save()} disabled={busy} className="btn btn-primary">
          <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz ceny'}
        </button>
      </div>
    </div>
  );
}


// =============================================================================
// Embedding Panel — wybór providera embeddingów + modelu z dropdown (filtered by capability)
// =============================================================================

interface EmbeddingPanelProps {
  onSaved: (msg: { type: 'ok' | 'err'; text: string }) => void;
}

function EmbeddingPanel({ onSaved }: EmbeddingPanelProps) {
  const [provider, setProvider] = useState('mock');
  const [geminiModel, setGeminiModel] = useState('');
  const [ollamaModel, setOllamaModel] = useState('');
  const [ollamaUrl, setOllamaUrl] = useState('');
  const [geminiModels, setGeminiModels] = useState<string[]>([]);
  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [cfg, gemList, ollList] = await Promise.all([
        admin.config.list('EMBEDDING'),
        admin.config.listModels('gemini', 'embedContent').catch(() => []),
        admin.config.listModels('ollama', 'embed').catch(() => []),
      ]);
      setProvider(cfg.find(e => e.key === 'embedding.provider')?.value ?? 'mock');
      setGeminiModel(cfg.find(e => e.key === 'gemini.embed-model')?.value ?? 'text-embedding-004');
      setOllamaModel(cfg.find(e => e.key === 'ai.ollama.embed-model')?.value ?? 'nomic-embed-text');
      setOllamaUrl(cfg.find(e => e.key === 'ai.ollama.url')?.value ?? 'http://ollama:11434');
      setGeminiModels(gemList.map(m => m.modelId));
      setOllamaModels(ollList.map(m => m.modelId));
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setLoading(false); }
  }

  async function discover(p: 'gemini' | 'ollama') {
    setBusy(true);
    try {
      const r = await admin.config.discoverModels(p);
      if (r.error) {
        onSaved({ type: 'err', text: `${p}: ${r.error}` });
      } else {
        // Reload list z DB (po discovery są tam zapisane)
        const list = await admin.config.listModels(p, p === 'gemini' ? 'embedContent' : 'embed');
        if (p === 'gemini') setGeminiModels(list.map(m => m.modelId));
        else setOllamaModels(list.map(m => m.modelId));
        onSaved({ type: 'ok', text: `Pobrano ${r.models.length} modeli z ${p}, embed: ${list.length}` });
      }
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function save() {
    setBusy(true);
    try {
      const r = await admin.config.bulk([
        { key: 'embedding.provider', value: provider },
        { key: 'gemini.embed-model', value: geminiModel },
        { key: 'ai.ollama.embed-model', value: ollamaModel },
        { key: 'ai.ollama.url', value: ollamaUrl },
      ]);
      onSaved({ type: 'ok', text: `Zaktualizowano ${r.zaktualizowane} ustawień.` });
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="space-y-5">
      <div className="card">
        <div className="card-head"><div className="card-title">Provider embeddingów RAG</div></div>
        <div className="card-body">
          <p className="text-[12px] text-ink-500 mb-3">
            Embedding 768-wymiarowy używany do wyszukiwania semantycznego (RAG).
            <strong> mock</strong> = deterministyczny pseudo-wektor (do testów),
            <strong> ollama</strong> = lokalnie, free,
            <strong> gemini</strong> = cloud, ~$0.025/1M tokens.
          </p>
          <div className="flex gap-2">
            {['mock', 'ollama', 'gemini'].map(p => (
              <button key={p} onClick={() => setProvider(p)}
                className={'btn btn-sm ' + (provider === p ? 'btn-primary' : 'btn-secondary')}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {provider === 'gemini' && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">Model Gemini do embeddingów</div>
            <button onClick={() => void discover('gemini')} disabled={busy}
              className="btn btn-secondary btn-sm ml-auto">
              <Icon name="sparkles" className="w-3.5 h-3.5"/> Pobierz z API
            </button>
          </div>
          <div className="card-body">
            <SearchableSelect value={geminiModel} onChange={setGeminiModel}>
              {geminiModels.length === 0 && <option value={geminiModel}>{geminiModel} (brak listy — wpisz ręcznie poniżej)</option>}
              {geminiModels.map(m => <option key={m} value={m}>{m}</option>)}
            </SearchableSelect>
            <input value={geminiModel} onChange={e => setGeminiModel(e.target.value)}
              className="input font-mono mt-2 text-[12px]" placeholder="lub wpisz custom" />
          </div>
        </div>
      )}

      {provider === 'ollama' && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">Model Ollama do embeddingów</div>
            <button onClick={() => void discover('ollama')} disabled={busy}
              className="btn btn-secondary btn-sm ml-auto">
              <Icon name="sparkles" className="w-3.5 h-3.5"/> Pobierz z API
            </button>
          </div>
          <div className="card-body grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Model</span>
              <SearchableSelect value={ollamaModel} onChange={setOllamaModel}>
                {ollamaModels.length === 0 && <option value={ollamaModel}>{ollamaModel} (brak — pull model przez ollama CLI)</option>}
                {ollamaModels.map(m => <option key={m} value={m}>{m}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">URL Ollama</span>
              <input value={ollamaUrl} onChange={e => setOllamaUrl(e.target.value)}
                className="input font-mono" placeholder="http://ollama:11434"/>
            </label>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={() => void load()} disabled={busy} className="btn btn-secondary">Odśwież</button>
        <button onClick={() => void save()} disabled={busy} className="btn btn-primary">
          <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Warstwy mapy: edytor URL/code/protocol + klucz API GUGiK
// ============================================================

interface MapsLayersPanelProps {
  onSaved: (m: { type: 'ok' | 'err'; text: string }) => void;
}

function MapsLayersPanel({ onSaved }: MapsLayersPanelProps) {
  const [layers, setLayers] = useState<import('@/types').MapLayer[]>([]);
  const [gugikEntries, setGugikEntries] = useState<ConfigEntry[]>([]);
  const [gugikDraft, setGugikDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<import('@/api/endpoints').MapLayerInput>>({});
  const [creating, setCreating] = useState(false);
  const [availability, setAvailability] = useState<Record<string, boolean>>({});

  async function load() {
    setLoading(true);
    try {
      const [list, gugik] = await Promise.all([
        (await import('@/api/endpoints')).mapy.layers(),
        admin.config.list('GUGIK'),
      ]);
      setLayers(list);
      setGugikEntries(gugik);
      const init: Record<string, string> = {};
      gugik.forEach(e => { init[e.key] = e.value; });
      setGugikDraft(init);
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function checkAvailability() {
    setBusy(true);
    try {
      const { mapy } = await import('@/api/endpoints');
      const a = await mapy.availability();
      setAvailability(a);
      onSaved({ type: 'ok', text: 'Sprawdzono dostępność wszystkich warstw.' });
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? 'Błąd' });
    } finally { setBusy(false); }
  }

  async function saveGugik() {
    setBusy(true);
    try {
      const zmiany = Object.entries(gugikDraft)
        .filter(([k, v]) => {
          const orig = gugikEntries.find(e => e.key === k);
          if (orig?.secret && v === '********') return false;
          return (orig?.value ?? '') !== v;
        })
        .map(([key, value]) => ({ key, value }));
      if (zmiany.length === 0) {
        onSaved({ type: 'ok', text: 'Brak zmian w sekcji GUGiK.' });
        setBusy(false);
        return;
      }
      const res = await admin.config.bulk(zmiany);
      onSaved({ type: 'ok', text: `Zapisano ${res.zaktualizowane} ustawień GUGiK.` });
      await load();
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? 'Błąd zapisu' });
    } finally { setBusy(false); }
  }

  function startEdit(w: import('@/types').MapLayer) {
    setEditing(w.id);
    setCreating(false);
    setDraft({
      code: w.code, name: w.name, source: w.source,
      protocol: (w.protocol as 'WMS' | 'WMTS' | 'XYZ') ?? 'WMS', url: w.url,
      layerId: w.layerId ?? '', imageFormat: w.imageFormat ?? 'image/png',
      tileMatrixSet: w.tileMatrixSet ?? '', wmtsStyle: w.wmtsStyle ?? '',
      category: w.category, defaultOpacity: w.defaultOpacity,
      enabledByDefault: w.enabledByDefault,
    });
  }

  function startCreate() {
    setEditing(null);
    setCreating(true);
    setDraft({
      code: '', name: '', source: '', protocol: 'WMS', url: '',
      layerId: '', imageFormat: 'image/png', category: 'Inne',
      defaultOpacity: 80, enabledByDefault: false,
    });
  }

  async function saveLayer() {
    setBusy(true);
    try {
      const { mapy } = await import('@/api/endpoints');
      if (creating) {
        if (!draft.code || !draft.name || !draft.url) {
          onSaved({ type: 'err', text: 'Code, name, url są wymagane' }); setBusy(false); return;
        }
        await mapy.createLayer(draft as import('@/api/endpoints').MapLayerInput);
        onSaved({ type: 'ok', text: 'Warstwa utworzona.' });
      } else if (editing != null) {
        await mapy.updateLayer(editing, draft);
        onSaved({ type: 'ok', text: 'Warstwa zaktualizowana.' });
      }
      setEditing(null); setCreating(false);
      await load();
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? 'Błąd zapisu' });
    } finally { setBusy(false); }
  }

  async function deleteLayer(w: import('@/types').MapLayer) {
    if (!confirm(`Usunąć warstwę "${w.name}" (${w.code})?`)) return;
    setBusy(true);
    try {
      const { mapy } = await import('@/api/endpoints');
      await mapy.deleteLayer(w.id);
      onSaved({ type: 'ok', text: 'Warstwa usunięta.' });
      await load();
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? 'Błąd usuwania' });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="flex flex-col gap-4">
      {/* GUGiK API key */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Klucz API GUGiK</div>
          <span className="ml-auto text-[11px] text-ink-400">Dla auth-protected serwisów geoportal.gov.pl (ORTO/BDOT10k/NMT)</span>
        </div>
        <div className="card-body flex flex-col gap-3">
          {gugikEntries.length === 0 && <div className="text-ink-400 text-[12px]">Brak konfiguracji w sekcji GUGIK.</div>}
          {gugikEntries.map(e => (
            <label key={e.key} className="field">
              <span className="field-label">{e.key}{e.secret && <span className="ml-1 text-ai-600">🔒</span>}</span>
              <input
                type={e.secret ? 'password' : 'text'}
                value={gugikDraft[e.key] ?? ''}
                onChange={ev => setGugikDraft({ ...gugikDraft, [e.key]: ev.target.value })}
                placeholder={e.secret ? '(sekret — wpisz nową wartość żeby zmienić)' : ''}
                className="input font-mono text-[12px]"
              />
              {e.description && <span className="text-[11px] text-ink-400 mt-1">{e.description}</span>}
            </label>
          ))}
          <div className="flex justify-end">
            <button onClick={() => void saveGugik()} disabled={busy} className="btn btn-primary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5"/> Zapisz GUGiK
            </button>
          </div>
        </div>
      </div>

      {/* Warstwy WMS/XYZ */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Warstwy mapy</div>
          <span className="ml-auto flex gap-2">
            <button onClick={() => void checkAvailability()} disabled={busy} className="btn btn-secondary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5"/> Sprawdź dostępność
            </button>
            <button onClick={startCreate} disabled={busy} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj warstwę
            </button>
          </span>
        </div>
        <div className="card-body !p-0">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-[11px] text-ink-500 uppercase">
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Nazwa</th>
                <th className="px-3 py-2">Protokół</th>
                <th className="px-3 py-2">Kategoria</th>
                <th className="px-3 py-2">Domyśl.</th>
                <th className="px-3 py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {layers.map(w => {
                const dot = w.code in availability ? (availability[w.code] ? 'sdot-green' : 'sdot-red') : 'sdot-gray';
                return (
                  <tr key={w.id} className="border-b border-ink-100 hover:bg-ink-50">
                    <td className="px-3 py-2"><span className={'sdot ' + dot}/></td>
                    <td className="px-3 py-2 font-mono">{w.code}</td>
                    <td className="px-3 py-2 truncate max-w-[280px]" title={w.url}>{w.name}</td>
                    <td className="px-3 py-2"><span className="pill pill-earth">{w.protocol}</span></td>
                    <td className="px-3 py-2 text-ink-500">{w.category}</td>
                    <td className="px-3 py-2">{w.enabledByDefault ? '✓' : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => startEdit(w)} className="btn btn-secondary btn-sm">Edytuj</button>
                      <button onClick={() => void deleteLayer(w)} className="btn btn-secondary btn-sm ml-1 text-red-500">Usuń</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edytor / dodawanie */}
      {(editing != null || creating) && (
        <div className="card border-water-500/30">
          <div className="card-head">
            <div className="card-title">{creating ? 'Nowa warstwa' : 'Edycja warstwy'}</div>
            <button onClick={() => { setEditing(null); setCreating(false); }} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="x" className="w-3.5 h-3.5"/> Anuluj
            </button>
          </div>
          <div className="card-body grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Code (unikalny)</span>
              <input value={draft.code ?? ''} onChange={e => setDraft({ ...draft, code: e.target.value })}
                className="input font-mono" placeholder="np. ORTO_NEW"/>
            </label>
            <label className="field">
              <span className="field-label">Nazwa</span>
              <input value={draft.name ?? ''} onChange={e => setDraft({ ...draft, name: e.target.value })}
                className="input" placeholder="np. Ortofotomapa GUGiK"/>
            </label>
            <label className="field">
              <span className="field-label">Źródło (właściciel)</span>
              <input value={draft.source ?? ''} onChange={e => setDraft({ ...draft, source: e.target.value })}
                className="input" placeholder="np. GUGiK"/>
            </label>
            <label className="field">
              <span className="field-label">Protokół</span>
              <SearchableSelect value={draft.protocol ?? 'WMS'} onChange={v => setDraft({ ...draft, protocol: v as 'WMS' | 'WMTS' | 'XYZ' })}>
                <option value="WMS">WMS (GetMap z bbox)</option>
                <option value="WMTS">WMTS (kafelki GetTile)</option>
                <option value="XYZ">XYZ tiles ({'{z}/{x}/{y}'})</option>
              </SearchableSelect>
            </label>
            <label className="field col-span-2">
              <span className="field-label">URL</span>
              <input value={draft.url ?? ''} onChange={e => setDraft({ ...draft, url: e.target.value })}
                className="input font-mono text-[11px]"
                placeholder={draft.protocol === 'XYZ'
                  ? 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
                  : draft.protocol === 'WMTS'
                    ? 'https://mapy.geoportal.gov.pl/wss/service/WMTS/guest/wmts/BDOT10k'
                    : 'https://example.gov.pl/wms?'}/>
            </label>
            <label className="field">
              <span className="field-label">LayerId (WMS/WMTS)</span>
              <input value={draft.layerId ?? ''} onChange={e => setDraft({ ...draft, layerId: e.target.value })}
                className="input font-mono" placeholder={draft.protocol === 'WMTS' ? 'np. ORTOFOTOMAPA' : 'np. Raster lub 0,1,2'}/>
            </label>
            {draft.protocol === 'WMTS' && (
              <>
                <label className="field">
                  <span className="field-label">TileMatrixSet (WMTS)</span>
                  <input value={draft.tileMatrixSet ?? ''} onChange={e => setDraft({ ...draft, tileMatrixSet: e.target.value })}
                    className="input font-mono" placeholder="EPSG:3857"/>
                </label>
                <label className="field">
                  <span className="field-label">Styl (WMTS)</span>
                  <input value={draft.wmtsStyle ?? ''} onChange={e => setDraft({ ...draft, wmtsStyle: e.target.value })}
                    className="input font-mono" placeholder="default"/>
                </label>
              </>
            )}
            <label className="field">
              <span className="field-label">Image Format</span>
              <SearchableSelect value={draft.imageFormat ?? 'image/png'}
                onChange={v => setDraft({ ...draft, imageFormat: v })}>
                <option value="image/png">image/png</option>
                <option value="image/jpeg">image/jpeg</option>
                <option value="image/webp">image/webp</option>
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Kategoria</span>
              <input value={draft.category ?? ''} onChange={e => setDraft({ ...draft, category: e.target.value })}
                className="input" placeholder="np. Topografia / Hydrogeologia / Tło"/>
            </label>
            <label className="field">
              <span className="field-label">Krycie domyślne (%)</span>
              <input type="number" min={0} max={100} value={draft.defaultOpacity ?? 80}
                onChange={e => setDraft({ ...draft, defaultOpacity: Number(e.target.value) })} className="input"/>
            </label>
            <label className="field col-span-2 flex flex-row items-center gap-2">
              <input type="checkbox" checked={Boolean(draft.enabledByDefault)}
                onChange={e => setDraft({ ...draft, enabledByDefault: e.target.checked })}/>
              <span>Zaznacz domyślnie w panelu warstw (auto-on przy otwarciu zakładki Mapy)</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 px-4 pb-4">
            <button onClick={() => void saveLayer()} disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : (creating ? 'Utwórz' : 'Zapisz zmiany')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PANEL: Numerator dokumentów
// ============================================================================
//
// Admin definiuje szablon numeracji dla 3 typów dokumentów: oferta / umowa /
// opracowanie. Szablon używa placeholderów obsługiwanych przez backendowy
// `DocumentNumberFormatter`:
//
//   {YYYY} – rok 4-cyfrowy (2026)
//   {YY}   – rok 2-cyfrowy (26)
//   {MM}   – miesiąc 2-cyfrowy (05)
//   {N}    – sekwencja bez paddingu (7)
//   {N:K}  – sekwencja z paddingiem K cyfr (np. {N:3} → 007)
//
// Live preview formatuje szablon klient-side z PRZYKŁADOWĄ sekwencją (np. 7) —
// to nie jest zapowiedź faktycznego następnego numeru z bazy, tylko demonstracja
// co user dostanie po zmianie. Dzięki temu nie potrzeba dodatkowego endpointa.
type DocumentNumberingPanelProps = { onSaved: (msg: { type: 'ok' | 'err'; text: string }) => void };

const NUMBERING_KEYS = [
  { key: 'doc.number.offer.format',    label: 'Oferta',      defaultValue: 'OF-{YYYY}-{N:3}' },
  { key: 'doc.number.contract.format', label: 'Umowa',       defaultValue: 'UM-{YYYY}-{N:3}' },
  { key: 'doc.number.study.format',    label: 'Opracowanie', defaultValue: 'PR-{YYYY}-{N:3}' },
  { key: 'doc.number.order.format',    label: 'Zlecenie na roboty', defaultValue: 'ZL/{YYYY}/{N:3}' },
] as const;

const PLACEHOLDER_DOCS: { ph: string; opis: string; example: string }[] = [
  { ph: '{YYYY}', opis: 'Rok 4-cyfrowy',     example: '2026' },
  { ph: '{YY}',   opis: 'Rok 2-cyfrowy',     example: '26' },
  { ph: '{MM}',   opis: 'Miesiąc 2-cyfrowy', example: '05' },
  { ph: '{N}',    opis: 'Sekwencja (bez paddingu)', example: '7' },
  { ph: '{N:3}',  opis: 'Sekwencja z paddingiem 3 cyfr',  example: '007' },
  { ph: '{N:5}',  opis: 'Sekwencja z paddingiem 5 cyfr',  example: '00007' },
];

const PRESET_TEMPLATES = [
  'OF-{YYYY}-{N:3}',
  'UM-{YYYY}-{N:3}',
  'PR-{YYYY}-{N:3}',
  '{YYYY}/OF/{N:4}',
  'OF/{YY}/{MM}/{N:3}',
  'OF-{N:5}',
];

/** Klient-side preview: ta sama logika co backendowy DocumentNumberFormatter.format(). */
function previewTemplate(template: string, exampleSequence = 7): { ok: true; preview: string } | { ok: false; error: string } {
  try {
    if (!template || !template.trim()) return { ok: false, error: 'Szablon jest pusty' };
    const matches = template.match(/\{N(?::(\d+))?\}/g) ?? [];
    if (matches.length === 0) return { ok: false, error: 'Brak placeholderu sekwencji {N} lub {N:K}' };
    if (matches.length > 1) return { ok: false, error: 'Tylko jeden placeholder sekwencji jest dozwolony' };
    const now = new Date();
    let result = template
      .replace(/\{YYYY\}/g, String(now.getFullYear()))
      .replace(/\{YY\}/g, String(now.getFullYear() % 100).padStart(2, '0'))
      .replace(/\{MM\}/g, String(now.getMonth() + 1).padStart(2, '0'));
    result = result.replace(/\{N(?::(\d+))?\}/g, (_, padStr) => {
      if (padStr) {
        const k = parseInt(padStr, 10);
        if (k < 1 || k > 10) throw new Error('Padding {N:K} musi być 1-10, jest: ' + k);
        return String(exampleSequence).padStart(k, '0');
      }
      return String(exampleSequence);
    });
    return { ok: true, preview: result };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? String(e) };
  }
}

function DocumentNumberingPanel({ onSaved }: DocumentNumberingPanelProps) {
  const [entries, setEntries] = useState<ConfigEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await admin.config.list('DOCUMENT_NUMBERING');
      setEntries(data);
      const d: Record<string, string> = {};
      // Wartości z backendu, z fallbackiem na default jeśli wpis nie istnieje (np. po świeżej instalacji bez migracji V49).
      NUMBERING_KEYS.forEach(k => {
        const found = data.find(e => e.key === k.key);
        d[k.key] = found?.value ?? k.defaultValue;
      });
      setDraft(d);
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? String(e) });
    } finally { setLoading(false); }
  }

  async function save() {
    // Walidacja klient-side przed wysłaniem — szybciej niż czekać na backend.
    for (const k of NUMBERING_KEYS) {
      const r = previewTemplate(draft[k.key] ?? '');
      if (!r.ok) {
        onSaved({ type: 'err', text: `Niepoprawny szablon dla ${k.label}: ${r.error}` });
        return;
      }
    }
    setBusy(true);
    try {
      const zmiany = NUMBERING_KEYS
        .map(k => ({ key: k.key, value: draft[k.key] ?? k.defaultValue }))
        .filter(({ key, value }) => {
          const orig = entries.find(e => e.key === key);
          return (orig?.value ?? '') !== value;
        });
      if (zmiany.length === 0) {
        onSaved({ type: 'ok', text: 'Brak zmian do zapisania.' });
        setBusy(false);
        return;
      }
      const res = await admin.config.bulk(zmiany);
      onSaved({ type: 'ok', text: `Zapisano ${res.zaktualizowane} szablon${res.zaktualizowane === 1 ? '' : 'y'}.` });
      await load();
    } catch (e: any) {
      onSaved({ type: 'err', text: e.message ?? 'Błąd zapisu' });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="card">
      <div className="card-body">
        <div className="text-[12px] text-ink-600 mb-4 p-3 bg-water-50 border border-water-200 rounded">
          <div className="font-semibold text-water-800 mb-1">Jak to działa</div>
          Każdy nowo tworzony dokument (oferta, umowa, opracowanie) dostaje numer wg szablonu poniżej.
          Zmiana szablonu wpływa tylko na DOKUMENTY UTWORZONE PÓŹNIEJ — istniejące zachowują swoje numery.
          Sekwencja resetuje się gdy zmienia się statyczny prefix szablonu (np. {'{YYYY}'} → wówczas
          numerator zaczyna od 1 dla nowego roku).
        </div>

        <div className="space-y-5">
          {NUMBERING_KEYS.map(k => {
            const value = draft[k.key] ?? '';
            const preview = previewTemplate(value);
            return (
              <div key={k.key} className="grid grid-cols-[140px_1fr] gap-4 items-start">
                <div className="pt-2">
                  <div className="font-semibold text-ink-900 text-[13px]">{k.label}</div>
                  <div className="text-[10px] text-ink-400 font-mono">{k.key}</div>
                </div>
                <div>
                  <input
                    value={value}
                    onChange={ev => setDraft({ ...draft, [k.key]: ev.target.value })}
                    className={'input font-mono text-[13px] ' + (preview.ok ? '' : 'border-red-400')}
                    placeholder={k.defaultValue}
                  />
                  {preview.ok ? (
                    <div className="mt-1.5 text-[12px] flex items-center gap-2">
                      <span className="text-ink-500">Przykładowy numer:</span>
                      <span className="font-mono font-semibold bg-ink-50 px-2 py-0.5 rounded text-ink-900">
                        {preview.preview}
                      </span>
                      <span className="text-[10px] text-ink-400">(z sekwencją 7)</span>
                    </div>
                  ) : (
                    <div className="mt-1.5 text-[12px] text-red-600 font-medium">
                      ⚠ {preview.error}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <hr className="border-0 border-t border-ink-100 my-5"/>

        <div className="grid grid-cols-2 gap-5 mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Placeholdery</div>
            <table className="w-full text-[12px]">
              <tbody>
                {PLACEHOLDER_DOCS.map(p => (
                  <tr key={p.ph} className="border-b border-ink-50 last:border-0">
                    <td className="py-1 font-mono text-ai-700 w-20">{p.ph}</td>
                    <td className="py-1 text-ink-700">{p.opis}</td>
                    <td className="py-1 font-mono text-ink-500 text-[11px] text-right">{p.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-2">Gotowe szablony (kliknij aby skopiować)</div>
            <div className="flex flex-col gap-1">
              {PRESET_TEMPLATES.map(t => {
                const p = previewTemplate(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(t)}
                    className="text-left px-2 py-1 rounded hover:bg-ink-50 text-[11px] flex items-center gap-2"
                    title={'Kliknij aby skopiować ' + t}
                  >
                    <span className="font-mono text-ink-800 flex-1">{t}</span>
                    <span className="text-ink-400">→</span>
                    <span className="font-mono font-semibold text-ink-900">{p.ok ? p.preview : '?'}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button disabled={busy} onClick={() => void save()} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> Zapisz szablony
          </button>
          <div className="flex-1"/>
          <div className="text-[11px] text-ink-400">
            Zmiana wpływa tylko na nowe dokumenty. Istniejące zachowują numery.
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PANEL: KSeF (faktury) — parametry integracji + wgranie certyfikatu przez pliki
// ============================================================================
//
// Konfiguracja KSeF wyłącznie tutaj (panel admina). Certyfikat uwierzytelniający
// wgrywamy przez ZAŁĄCZENIE plików (bez wklejania Base64): albo osobne .key + .crt,
// albo gotowy pakiet .p12/.pfx. Backend buduje/waliduje keystore PKCS#12 i zapisuje
// w zaszyfrowanych kluczach ksef.cert.p12 / ksef.cert.password.
type KsefPanelProps = { onSaved: (msg: { type: 'ok' | 'err'; text: string }) => void };

function KsefPanel({ onSaved }: KsefPanelProps) {
  // Wgranie certyfikatu KSeF jest po stronie API pod ADMIN_SECRETS — front pyta o to samo.
  const { user } = useAuth();
  const mozeSekrety = hasPermission(user, 'ADMIN_SECRETS');
  const [mode, setMode] = useState('mock');
  const [url, setUrl] = useState('');
  const [nip, setNip] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [certMode, setCertMode] = useState<'files' | 'p12'>('files');
  const [keyFile, setKeyFile] = useState<File | undefined>();
  const [crtFile, setCrtFile] = useState<File | undefined>();
  const [p12File, setP12File] = useState<File | undefined>();
  const [pass, setPass] = useState('');
  const [certInfo, setCertInfo] = useState<KsefCertInfo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [cfg, info] = await Promise.all([
        admin.config.list('KSEF'),
        admin.config.ksefCertInfo().catch(() => null),
      ]);
      const get = (k: string) => cfg.find(e => e.key === k)?.value ?? '';
      setMode(get('ksef.mode') || 'mock');
      setUrl(get('ksef.env.url'));
      setNip(get('ksef.nip'));
      setCertInfo(info);
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setLoading(false); }
  }

  async function saveSettings() {
    setBusy(true);
    try {
      const r = await admin.config.bulk([
        { key: 'ksef.mode', value: mode },
        { key: 'ksef.env.url', value: url.trim() },
        { key: 'ksef.nip', value: nip.trim() },
      ]);
      onSaved({ type: 'ok', text: `Zapisano ${r.zaktualizowane} ustawień KSeF.` });
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function uploadCert() {
    if (!pass.trim()) { onSaved({ type: 'err', text: 'Podaj hasło do certyfikatu.' }); return; }
    if (certMode === 'files' && (!keyFile || !crtFile)) {
      onSaved({ type: 'err', text: 'Załącz oba pliki: klucz (.key) i certyfikat (.crt).' }); return;
    }
    if (certMode === 'p12' && !p12File) {
      onSaved({ type: 'err', text: 'Załącz plik .p12/.pfx.' }); return;
    }
    // Limit rozmiaru — blokuj, jeśli którykolwiek załączony plik przekracza 100 MB
    const files = [keyFile, crtFile, p12File].filter(Boolean) as File[];
    const sizeErr = validateUploadSize(files);
    if (sizeErr) { onSaved({ type: 'err', text: sizeErr }); return; }
    setUploading(true);
    try {
      const r = await admin.config.uploadKsefCertificate(
        certMode === 'files'
          ? { key: keyFile, crt: crtFile, password: pass }
          : { p12: p12File, password: pass });
      setCertInfo(r.certificate ?? null);
      setKeyFile(undefined); setCrtFile(undefined); setP12File(undefined); setPass('');
      setResetKey(k => k + 1);
      onSaved({ type: 'ok', text: 'Certyfikat KSeF zapisany (zaszyfrowany AES-256-GCM).' });
    } catch (e: any) {
      onSaved({ type: 'err', text: e?.message ?? String(e) });
    } finally { setUploading(false); }
  }

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="space-y-5 max-w-[720px]">
      {/* Parametry integracji */}
      <div className="card">
        <div className="card-head"><div className="card-title">Parametry integracji</div></div>
        <div className="card-body grid gap-3">
          <label className="field">
            <span className="field-label">Tryb</span>
            <SearchableSelect value={mode} onChange={setMode}>
              <option value="mock">mock (dane próbne — bez KSeF)</option>
              <option value="test">test (środowisko testowe)</option>
              <option value="demo">demo (przedprodukcja — bez skutków podatkowych)</option>
              <option value="prod">prod (produkcja)</option>
              <option value="off">off (wyłączone)</option>
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">URL środowiska (test/prod)</span>
            <input value={url} onChange={e => setUrl(e.target.value)} className="input font-mono text-[12px]"
              placeholder="https://api-test.ksef.mf.gov.pl" />
          </label>
          <label className="field">
            <span className="field-label">NIP podatnika</span>
            <input value={nip} onChange={e => setNip(e.target.value)} className="input font-mono" placeholder="np. 1234567890" />
          </label>
          <div className="flex justify-end">
            <button disabled={busy} onClick={() => void saveSettings()} className="btn btn-primary btn-sm">
              <Icon name="check" className="w-3.5 h-3.5" /> Zapisz parametry
            </button>
          </div>
        </div>
      </div>

      {/* Certyfikat */}
      <div className="card">
        <div className="card-head">
          <div className="card-title">Certyfikat uwierzytelniający</div>
          {certInfo?.set
            ? <span className={'ml-auto pill ' + (certInfo.expired ? 'pill-red' : 'pill-earth')}>
                {certInfo.expired ? 'wygasł' : 'ustawiony'}
              </span>
            : <span className="ml-auto pill pill-gray">brak</span>}
        </div>
        <div className="card-body grid gap-3">
          {certInfo?.set && (
            <div className={'text-[11px] rounded p-2 border ' +
              (certInfo.expired ? 'bg-red-50 border-red-200 text-red-700' : 'bg-ink-50 border-ink-100 text-ink-600')}>
              {certInfo.subject
                ? <>
                    <div><span className="text-ink-400">Podmiot:</span> <span className="font-mono">{certInfo.subject}</span></div>
                    {certInfo.validTo && <div><span className="text-ink-400">Ważny do:</span> <span className="font-mono">{certInfo.validTo}</span>{certInfo.expired && ' — wymień certyfikat'}</div>}
                  </>
                : <>Certyfikat jest zapisany (metadanych nie udało się odczytać obecnym hasłem).</>}
            </div>
          )}

          <div className="flex gap-1 bg-ink-50 p-1 rounded-md w-fit">
            <button onClick={() => setCertMode('files')}
              className={'btn btn-sm ' + (certMode === 'files' ? 'btn-primary' : 'btn-ghost')}>Osobne pliki (.key + .crt)</button>
            <button onClick={() => setCertMode('p12')}
              className={'btn btn-sm ' + (certMode === 'p12' ? 'btn-primary' : 'btn-ghost')}>Pakiet .p12 / .pfx</button>
          </div>

          {certMode === 'files' ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="field">
                <span className="field-label">Klucz prywatny (.key)</span>
                <input key={'k' + resetKey} type="file" accept=".key,.pem,.txt" className="text-[12px]"
                  onChange={e => setKeyFile(e.target.files?.[0])} />
                {keyFile && <span className="text-[11px] text-ink-500 mt-0.5">Wybrano: {keyFile.name}</span>}
              </label>
              <label className="field">
                <span className="field-label">Certyfikat (.crt)</span>
                <input key={'c' + resetKey} type="file" accept=".crt,.cer,.pem,.txt" className="text-[12px]"
                  onChange={e => setCrtFile(e.target.files?.[0])} />
                {crtFile && <span className="text-[11px] text-ink-500 mt-0.5">Wybrano: {crtFile.name}</span>}
              </label>
            </div>
          ) : (
            <label className="field">
              <span className="field-label">Pakiet PKCS#12 (.p12 / .pfx)</span>
              <input key={'p' + resetKey} type="file" accept=".p12,.pfx" className="text-[12px]"
                onChange={e => setP12File(e.target.files?.[0])} />
              {p12File && <span className="text-[11px] text-ink-500 mt-0.5">Wybrano: {p12File.name}</span>}
            </label>
          )}

          <label className="field">
            <span className="field-label">Hasło do certyfikatu</span>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} className="input"
              placeholder="hasło ustawione przy generowaniu certyfikatu" autoComplete="new-password" />
          </label>

          <div className="text-[11px] text-ink-400">
            Pliki są przesyłane jako załączniki i nigdzie nie są przechowywane w oryginale — backend buduje z nich
            keystore PKCS#12 zaszyfrowany AES-256-GCM. Tryb <strong>mock</strong> działa bez certyfikatu.
          </div>
          <div className="flex justify-end">
            <button disabled={uploading || !mozeSekrety} onClick={() => void uploadCert()}
                    title={mozeSekrety ? undefined : 'Wymaga uprawnienia „Klucze API, hasło SMTP, certyfikat KSeF"'}
                    className="btn btn-primary btn-sm">
              <Icon name="cloud" className="w-3.5 h-3.5" /> {uploading ? 'Wgrywanie…' : 'Wgraj certyfikat'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
